import type { JSX } from 'react';

import {
  AccordionDemos,
  AlertDemos,
  AutocompleteDemos,
  AvatarDemos,
  BadgeDemos,
  BadgeStatusDemos,
  BoxDemos,
  BreadcrumbsDemos,
  ButtonDemos,
  ButtonDisabledDemos,
  CardDeliveryPatternDemos,
  CardElevationDemos,
  CardNotificationDemos,
  CardPhasePatternDemos,
  CardUpNextPatternDemos,
  CardWithTitleDemos,
  CardWithoutTitleDemos,
  CheckboxDemos,
  ChipDemos,
  ChipToneDemos,
  ConfirmDialogDemos,
  ContainerDemos,
  DropdownMenuDemos,
  FlexColumnDemos,
  FlexRowDemos,
  FormAtomsDemos,
  FormFieldErrorDemos,
  FormFieldHelpDemos,
  GridDemos,
  HeadingDemos,
  HeadingDisplayDemos,
  HeadingScaleDemos,
  IconButtonDemos,
  ListDemos,
  ModalDemos,
  MultiSelectDemos,
  RadioGroupDemos,
  SectionDemos,
  SelectDemos,
  SeparatorDemos,
  SnackbarDemos,
  SpinnerDemos,
  SpinnerLoadingMessageDemos,
  SpinnerMessageDemos,
  TableDemos,
  TableLoadingDemos,
  TableWithActionsDemos,
  TabsDemos,
  TextareaDemos,
  TextDemos,
  TextInputDemos,
  TooltipDemos,
} from './demos.js';

export type ComponentCategory =
  | 'typography'
  | 'inputs'
  | 'data-display'
  | 'feedback'
  | 'surfaces'
  | 'navigation'
  | 'layout';

export interface PropDoc {
  name: string;
  type: string;
  default?: string;
  required?: boolean;
  description: string;
}

export interface ComponentSection {
  id: string;
  title: string;
  code: string;
  Demo: () => JSX.Element;
}

export interface ComponentDocEntry {
  name: string;
  slug: string;
  category: ComponentCategory;
  status: 'stable' | 'planned';
  description: string;
  importExample: string;
  props: PropDoc[];
  sections: ComponentSection[];
}

/** Single source of truth for navigation, demos, and API docs. */
export const COMPONENT_REGISTRY: ComponentDocEntry[] = [
  {
    name: 'Button',
    slug: 'button',
    category: 'inputs',
    status: 'stable',
    description:
      'Standard button for platform apps. Pill radius, coral primary, 2px forest secondary border, and underlined forest link — design-system visual language at admin density.',
    importExample: "import { Button } from '@nrx/ui';",
    props: [
      {
        name: 'children',
        type: 'ReactNode',
        required: true,
        description: 'Button label or content.',
      },
      {
        name: 'tone',
        type: "'primary' | 'secondary' | 'ghostOnDark' | 'link'",
        default: "'primary'",
        description:
          'Semantic tone hint for styling. Primary applies coral background and `--shadow-cta` elevation from theme.css.',
      },
      {
        name: 'className',
        type: 'string',
        description: 'Additional CSS classes for visual styling.',
      },
      {
        name: 'disabled',
        type: 'boolean',
        description: 'Disables the button and prevents interaction.',
      },
      {
        name: 'type',
        type: "'button' | 'submit' | 'reset'",
        default: "'button'",
        description: 'Native button type attribute.',
      },
    ],
    sections: [
      {
        id: 'tones',
        title: 'Button tones',
        code: `import { Button } from '@nrx/ui';

<Button tone="primary">Primary button</Button>
<Button tone="secondary">Secondary button</Button>
<Button tone="link">Learn more</Button>`,
        Demo: ButtonDemos,
      },
      {
        id: 'disabled',
        title: 'Disabled button',
        code: `import { Button } from '@nrx/ui';

<Button tone="primary" disabled>
  Save
</Button>
<Button tone="secondary" disabled>
  Cancel
</Button>`,
        Demo: ButtonDisabledDemos,
      },
    ],
  },
  {
    name: 'TextInput',
    slug: 'text-input',
    category: 'inputs',
    status: 'stable',
    description:
      'Controlled text input with a string-based onChange callback. Design-system visual language at admin density: 16px radius, forest focus halo, coral error border.',
    importExample: "import { TextInput } from '@nrx/ui';",
    props: [
      {
        name: 'value',
        type: 'string',
        required: true,
        description: 'Current input value (controlled).',
      },
      {
        name: 'onChange',
        type: '(value: string) => void',
        required: true,
        description: 'Called when the input value changes.',
      },
      {
        name: 'placeholder',
        type: 'string',
        description: 'Placeholder text when empty.',
      },
      {
        name: 'disabled',
        type: 'boolean',
        description: 'Disables the input.',
      },
      {
        name: 'className',
        type: 'string',
        description: 'Additional CSS classes.',
      },
      {
        name: 'type',
        type: "'text' | 'email' | 'password'",
        default: "'text'",
        description: 'Native input type.',
      },
    ],
    sections: [
      {
        id: 'controlled',
        title: 'Controlled input',
        code: `import { TextInput } from '@nrx/ui';

const [value, setValue] = useState('');

<TextInput value={value} onChange={setValue} placeholder="Type here…" />
<TextInput
  value={value}
  onChange={setValue}
  type="email"
  placeholder="you@example.com"
/>
<TextInput value={value} onChange={setValue} disabled />`,
        Demo: TextInputDemos,
      },
    ],
  },
  {
    name: 'FormField',
    slug: 'form-field',
    category: 'inputs',
    status: 'stable',
    description:
      'Accessible form control composing a bold forest label, TextInput, optional help text, and validation error. Error sets aria-invalid, which drives the coral input border.',
    importExample: "import { FormField } from '@nrx/ui';",
    props: [
      {
        name: 'label',
        type: 'string',
        required: true,
        description: 'Visible label associated with the input via htmlFor/id.',
      },
      {
        name: 'value',
        type: 'string',
        required: true,
        description: 'Current input value (controlled).',
      },
      {
        name: 'onChange',
        type: '(value: string) => void',
        required: true,
        description: 'Called when the input value changes.',
      },
      {
        name: 'error',
        type: 'string',
        description: 'Validation message; sets aria-invalid and links via aria-describedby.',
      },
      {
        name: 'help',
        type: 'string',
        description: 'Supplementary hint rendered below the input.',
      },
      {
        name: 'placeholder',
        type: 'string',
        description: 'Placeholder text when empty.',
      },
      {
        name: 'disabled',
        type: 'boolean',
        description: 'Disables the input.',
      },
      {
        name: 'type',
        type: "'text' | 'email' | 'password'",
        default: "'text'",
        description: 'Native input type.',
      },
    ],
    sections: [
      {
        id: 'help',
        title: 'Field with help text',
        code: `import { FormField } from '@nrx/ui';

const [email, setEmail] = useState('');

<FormField
  label="Email"
  type="email"
  value={email}
  onChange={setEmail}
  placeholder="you@example.com"
  help="We will never share your email."
/>`,
        Demo: FormFieldHelpDemos,
      },
      {
        id: 'error',
        title: 'Field with error',
        code: `import { FormField } from '@nrx/ui';

const [email, setEmail] = useState('not-an-email');

<FormField
  label="Email"
  type="email"
  value={email}
  onChange={setEmail}
  placeholder="you@example.com"
  error="Please enter a valid email address."
/>`,
        Demo: FormFieldErrorDemos,
      },
    ],
  },
  {
    name: 'Badge',
    slug: 'badge',
    category: 'data-display',
    status: 'stable',
    description:
      'Inline status pill with semantic color variants, soft/solid/outline emphases, and an optional leading status dot.',
    importExample: "import { Badge } from '@nrx/ui';",
    props: [
      {
        name: 'children',
        type: 'ReactNode',
        required: true,
        description: 'Badge label text.',
      },
      {
        name: 'variant',
        type: "'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent'",
        default: "'neutral'",
        description: 'Semantic color variant. Info is brand blue; accent is coral.',
      },
      {
        name: 'emphasis',
        type: "'soft' | 'solid' | 'outline'",
        default: "'soft'",
        description: 'Fill style — soft wash (default), solid fill, or outline.',
      },
      {
        name: 'dot',
        type: 'boolean',
        default: 'false',
        description: 'Renders a leading status indicator dot.',
      },
      {
        name: 'className',
        type: 'string',
        description: 'Additional CSS classes.',
      },
    ],
    sections: [
      {
        id: 'variants',
        title: 'Variants',
        code: `import { Badge } from '@nrx/ui';

<Badge variant="neutral">Neutral</Badge>
<Badge variant="success">Success</Badge>
<Badge variant="warning">Warning</Badge>
<Badge variant="danger">Danger</Badge>
<Badge variant="info">Info</Badge>
<Badge variant="accent">Accent</Badge>`,
        Demo: BadgeDemos,
      },
      {
        id: 'status-pills',
        title: 'Status and membership pills',
        code: `import { Badge } from '@nrx/ui';

<Badge variant="success" dot>Delivered</Badge>
<Badge variant="accent" dot>On its way</Badge>
<Badge variant="accent" emphasis="solid" dot>Delayed</Badge>
<Badge variant="info" dot>Processing</Badge>
<Badge variant="info" emphasis="solid">Active member</Badge>
<Badge variant="accent" emphasis="outline">Featured</Badge>`,
        Demo: BadgeStatusDemos,
      },
    ],
  },
  {
    name: 'Avatar',
    slug: 'avatar',
    category: 'data-display',
    status: 'stable',
    description: 'Circular avatar with optional image and initials fallback.',
    importExample: "import { Avatar } from '@nrx/ui';",
    props: [
      {
        name: 'alt',
        type: 'string',
        required: true,
        description: 'Display name for alt text and initials fallback.',
      },
      {
        name: 'src',
        type: 'string',
        description: 'Optional profile photo URL.',
      },
      {
        name: 'size',
        type: "'sm' | 'md' | 'lg'",
        default: "'md'",
        description: 'Avatar diameter.',
      },
    ],
    sections: [
      {
        id: 'sizes',
        title: 'Sizes and fallback',
        code: `import { Avatar } from '@nrx/ui';

<Avatar alt="Jane Doe" size="sm" />
<Avatar alt="Jane Doe" />
<Avatar alt="Jane Doe" size="lg" />
<Avatar alt="Jane Doe" src="/photo.jpg" />`,
        Demo: AvatarDemos,
      },
    ],
  },
  {
    name: 'Card',
    slug: 'card',
    category: 'surfaces',
    status: 'stable',
    description:
      'Borderless container surface at 16px radius with design-system elevation levels and an optional title. Pattern sections below are composition recipes for docs — not exported components.',
    importExample: "import { Card } from '@nrx/ui';",
    props: [
      {
        name: 'children',
        type: 'ReactNode',
        required: true,
        description: 'Card body content.',
      },
      {
        name: 'title',
        type: 'ReactNode',
        description: 'Optional heading rendered above the body.',
      },
      {
        name: 'headingLevel',
        type: '2 | 3 | 4 | 5 | 6',
        default: '3',
        description: 'Semantic heading level for the optional title.',
      },
      {
        name: 'elevation',
        type: "'flat' | 'soft' | 'card' | 'pop'",
        default: "'card'",
        description:
          'Shadow level. flat is border-only; soft for list cards; card (default) for hero/modals; pop for menus/toasts.',
      },
      {
        name: 'className',
        type: 'string',
        description: 'Additional CSS classes for the card container.',
      },
    ],
    sections: [
      {
        id: 'with-title',
        title: 'Card with title',
        code: `import { Card } from '@nrx/ui';

<Card title="Card title">
  <p>Card body content goes here.</p>
</Card>`,
        Demo: CardWithTitleDemos,
      },
      {
        id: 'without-title',
        title: 'Card without title',
        code: `import { Card } from '@nrx/ui';

<Card>
  <p>Card body content without a title heading.</p>
</Card>`,
        Demo: CardWithoutTitleDemos,
      },
      {
        id: 'elevation',
        title: 'Elevation',
        code: `import { Card } from '@nrx/ui';

<Card elevation="flat">Flat</Card>
<Card elevation="soft">Soft</Card>
<Card elevation="card">Card</Card>
<Card elevation="pop">Pop</Card>`,
        Demo: CardElevationDemos,
      },
      {
        id: 'pattern-phase',
        title: 'Pattern: phase progress',
        code: `import { Card, Text } from '@nrx/ui';

{/* Composition recipe — not an exported PhaseCard */}
<Card>
  <div className="flex items-center gap-3">
    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-teal-soft)] text-lg font-bold">
      1
    </div>
    <div>
      <Text as="p" size="5" weight="bold">Phase 1</Text>
      <Text as="p" size="2" color="muted">Meals · Week 3 of 4</Text>
    </div>
  </div>
  {/* progress track + meta */}
</Card>`,
        Demo: CardPhasePatternDemos,
      },
      {
        id: 'pattern-delivery',
        title: 'Pattern: delivery status',
        code: `import { Button, Card, Text } from '@nrx/ui';
import { Truck } from '@nrx/ui/icons';

{/* Composition recipe — not an exported DeliveryCard */}
<Card className="overflow-hidden p-0">
  <div className="bg-[var(--color-action-primary)] px-4 py-3.5 text-white">In transit</div>
  {/* truck row, progress, Track footer */}
</Card>`,
        Demo: CardDeliveryPatternDemos,
      },
      {
        id: 'pattern-up-next',
        title: 'Pattern: up next',
        code: `import { Card, Text } from '@nrx/ui';
import { Truck } from '@nrx/ui/icons';

{/* Composition recipe — not an exported UpNextCard */}
<Card
  className="overflow-hidden p-0"
  style={{ background: 'linear-gradient(160deg, var(--color-brand-blue-soft), #C7E5E1)' }}
>
  {/* Up next pill, truck, three-segment progress */}
</Card>`,
        Demo: CardUpNextPatternDemos,
      },
      {
        id: 'pattern-notification',
        title: 'Pattern: notifications',
        code: `import { Button, Card, Text } from '@nrx/ui';
import { Calendar, X } from '@nrx/ui/icons';

{/* Composition recipe — not an exported NotificationCard */}
<Card elevation="flat" className="relative flex gap-3.5">
  <span className="absolute left-2 top-4 size-2 rounded-full bg-[var(--color-coral)]" />
  <Calendar size={32} />
  {/* title, body, Join, meta, dismiss */}
</Card>`,
        Demo: CardNotificationDemos,
      },
    ],
  },
  {
    name: 'Alert',
    slug: 'alert',
    category: 'feedback',
    status: 'stable',
    description: 'Inline feedback banner for info, warning, danger, and success states.',
    importExample: "import { Alert } from '@nrx/ui';",
    props: [
      {
        name: 'children',
        type: 'ReactNode',
        required: true,
        description: 'Alert message content.',
      },
      {
        name: 'tone',
        type: "'info' | 'warning' | 'danger' | 'success'",
        default: "'info'",
        description: 'Semantic tone mapped to `.alert--*` classes.',
      },
      {
        name: 'className',
        type: 'string',
        description: 'Additional CSS classes.',
      },
    ],
    sections: [
      {
        id: 'variants',
        title: 'Variants',
        code: `import { Alert } from '@nrx/ui';

<Alert tone="info">Informational message for the user.</Alert>
<Alert tone="warning">Warning — review before continuing.</Alert>
<Alert tone="danger">Something went wrong. Try again.</Alert>
<Alert tone="success">Changes saved successfully.</Alert>`,
        Demo: AlertDemos,
      },
    ],
  },
  {
    name: 'Spinner',
    slug: 'spinner',
    category: 'feedback',
    status: 'stable',
    description:
      'Loading indicator for explicit async UI states. Prefer with loadingMessage({ resource, operation }) when the call site knows both (ADR-0016).',
    importExample: "import { Spinner, loadingMessage } from '@nrx/ui';",
    props: [
      {
        name: 'label',
        type: 'string',
        default: "message ?? 'Loading'",
        description: 'Accessible label for assistive technology.',
      },
      {
        name: 'message',
        type: 'string',
        description: 'Optional visible loading copy shown beside the glyph.',
      },
      {
        name: 'className',
        type: 'string',
        description: 'Additional CSS classes.',
      },
    ],
    sections: [
      {
        id: 'basic',
        title: 'Basic spinner',
        code: `import { Spinner } from '@nrx/ui';

<Spinner label="Loading content" />`,
        Demo: SpinnerDemos,
      },
      {
        id: 'with-message',
        title: 'Visible message',
        code: `import { Spinner } from '@nrx/ui';

<Spinner message="Loading content…" />`,
        Demo: SpinnerMessageDemos,
      },
      {
        id: 'loading-message',
        title: 'Resource + operation messages',
        code: `import { Flex, Spinner, loadingMessage } from '@nrx/ui';

<Flex direction="column" gap="3">
  <Spinner message={loadingMessage({ resource: 'answer', operation: 'list' })} />
  <Spinner message={loadingMessage({ resource: 'answer', operation: 'create' })} />
  <Spinner message={loadingMessage({ resource: 'answer', operation: 'update' })} />
</Flex>`,
        Demo: SpinnerLoadingMessageDemos,
      },
    ],
  },
  {
    name: 'Box',
    slug: 'box',
    category: 'layout',
    status: 'stable',
    description: 'Generic layout box with spacing props mapped to design tokens.',
    importExample: "import { Box } from '@nrx/ui';",
    props: [
      { name: 'children', type: 'ReactNode', description: 'Box content.' },
      {
        name: 'as',
        type: 'keyof JSX.IntrinsicElements',
        default: "'div'",
        description: 'Polymorphic element tag.',
      },
      {
        name: 'p / px / py / pt / pr / pb / pl',
        type: 'SpacingScale',
        description: 'Padding on the spacing scale 0–11.',
      },
      {
        name: 'm / mx / my / mt / mr / mb / ml',
        type: 'SpacingScale',
        description: 'Margin on the spacing scale 0–11.',
      },
    ],
    sections: [
      {
        id: 'box',
        title: 'Box padding',
        code: `import { Box, Text } from '@nrx/ui';

<Box p="4">
  <Text size="2">Box with p="4" (16px padding)</Text>
</Box>`,
        Demo: BoxDemos,
      },
    ],
  },
  {
    name: 'Flex',
    slug: 'flex',
    category: 'layout',
    status: 'stable',
    description: 'Flexbox layout primitive with direction, alignment, and gap props.',
    importExample: "import { Flex } from '@nrx/ui';",
    props: [
      {
        name: 'direction',
        type: "'row' | 'column' | 'row-reverse' | 'column-reverse'",
        default: "'row'",
        description: 'Flex direction.',
      },
      {
        name: 'align',
        type: "'start' | 'center' | 'end' | 'baseline' | 'stretch'",
        description: 'Align items.',
      },
      {
        name: 'justify',
        type: "'start' | 'center' | 'end' | 'between'",
        description: 'Justify content.',
      },
      { name: 'gap', type: 'SpacingScale', description: 'Gap between flex items.' },
    ],
    sections: [
      {
        id: 'flex-row',
        title: 'Flex row',
        code: `import { Box, Flex, Text } from '@nrx/ui';

<Flex direction="row" align="center" gap="4">
  <Box p="2">
    <Text size="2">Item A</Text>
  </Box>
  <Box p="2">
    <Text size="2">Item B</Text>
  </Box>
  <Box p="2">
    <Text size="2">Item C</Text>
  </Box>
</Flex>`,
        Demo: FlexRowDemos,
      },
      {
        id: 'flex-column',
        title: 'Flex column',
        code: `import { Box, Flex, Text } from '@nrx/ui';

<Flex direction="column" align="stretch" gap="3">
  <Box p="2">
    <Text size="2">Item A</Text>
  </Box>
  <Box p="2">
    <Text size="2">Item B</Text>
  </Box>
  <Box p="2">
    <Text size="2">Item C</Text>
  </Box>
</Flex>`,
        Demo: FlexColumnDemos,
      },
    ],
  },
  {
    name: 'Grid',
    slug: 'grid',
    category: 'layout',
    status: 'stable',
    description: 'CSS grid layout primitive with column and row templates.',
    importExample: "import { Grid } from '@nrx/ui';",
    props: [
      { name: 'columns', type: 'number | string', description: 'Grid template columns.' },
      { name: 'rows', type: 'number | string', description: 'Grid template rows.' },
      { name: 'gap', type: 'SpacingScale', description: 'Grid gap.' },
    ],
    sections: [
      {
        id: 'grid',
        title: 'Grid layout',
        code: `import { Box, Grid, Text } from '@nrx/ui';

<Grid columns={2} gap="3">
  <Box p="3">
    <Text size="2">Cell 1</Text>
  </Box>
  <Box p="3">
    <Text size="2">Cell 2</Text>
  </Box>
  <Box p="3">
    <Text size="2">Cell 3</Text>
  </Box>
  <Box p="3">
    <Text size="2">Cell 4</Text>
  </Box>
</Grid>`,
        Demo: GridDemos,
      },
    ],
  },
  {
    name: 'Container',
    slug: 'container',
    category: 'layout',
    status: 'stable',
    description: 'Centered content container with max-width presets.',
    importExample: "import { Container } from '@nrx/ui';",
    props: [
      {
        name: 'size',
        type: "'1' | '2' | '3' | '4'",
        default: "'4'",
        description: 'Max-width preset.',
      },
    ],
    sections: [
      {
        id: 'container',
        title: 'Container width',
        code: `import { Box, Container, Text } from '@nrx/ui';

<Container size="3">
  <Box p="4">
    <Text size="2">Container size="3" — max-width 880px</Text>
  </Box>
</Container>`,
        Demo: ContainerDemos,
      },
    ],
  },
  {
    name: 'Section',
    slug: 'section',
    category: 'layout',
    status: 'stable',
    description: 'Vertical section wrapper with padding presets.',
    importExample: "import { Section } from '@nrx/ui';",
    props: [
      {
        name: 'size',
        type: "'1' | '2' | '3' | '4'",
        default: "'2'",
        description: 'Vertical padding preset.',
      },
    ],
    sections: [
      {
        id: 'section',
        title: 'Section padding',
        code: `import { Section, Text } from '@nrx/ui';

<Section size="1">
  <Text size="2">Section size="1"</Text>
</Section>
<Section size="2">
  <Text size="2">Section size="2"</Text>
</Section>
<Section size="3">
  <Text size="2">Section size="3"</Text>
</Section>
<Section size="4">
  <Text size="2">Section size="4"</Text>
</Section>`,
        Demo: SectionDemos,
      },
    ],
  },
  {
    name: 'Text',
    slug: 'text',
    category: 'typography',
    status: 'stable',
    description: 'Body text primitive with semantic size and color tokens.',
    importExample: "import { Text } from '@nrx/ui';",
    props: [
      { name: 'size', type: "'1'–'9'", default: "'3'", description: 'Type scale size.' },
      {
        name: 'color',
        type: "'default' | 'muted' | 'success' | 'warning' | 'danger' | 'info'",
        default: "'default'",
        description: 'Semantic text color.',
      },
      {
        name: 'weight',
        type: "'regular' | 'medium' | 'bold'",
        default: "'regular'",
        description: 'Font weight.',
      },
    ],
    sections: [
      {
        id: 'text',
        title: 'Text styles',
        code: `import { Text } from '@nrx/ui';

<Text as="p" size="3">
  Default body text (size 3)
</Text>
<Text as="p" size="2" color="muted">
  Muted supporting text (size 2)
</Text>
<Text as="p" size="2" color="success">
  Success message
</Text>
<Text as="p" size="2" color="danger">
  Error message
</Text>`,
        Demo: TextDemos,
      },
    ],
  },
  {
    name: 'Heading',
    slug: 'heading',
    category: 'typography',
    status: 'stable',
    description:
      'Heading primitive with semantic level, numeric type scale, and design-system brand scale (D1–H7). H2 (48px) and H4 (30px) are platform interpolations — the deck does not define them. Self-hosted Roboto ships 400–700 only (no Light 300).',
    importExample: "import { Heading } from '@nrx/ui';",
    props: [
      { name: 'as', type: "'h1'–'h6'", default: "'h1'", description: 'Semantic heading level.' },
      { name: 'size', type: "'1'–'9'", description: 'Type scale size; defaults by heading level.' },
      {
        name: 'scale',
        type: "'d1' | 'd2' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'h7'",
        description:
          'Design-system brand scale (display and heading steps). Overrides `size` when set.',
      },
      {
        name: 'weight',
        type: "'regular' | 'medium' | 'bold'",
        default: "'bold'",
        description: 'Font weight (400–700). Light 300 is not available.',
      },
    ],
    sections: [
      {
        id: 'heading',
        title: 'Heading levels',
        code: `import { Heading } from '@nrx/ui';

<Heading as="h1" size="8">
  Page title (h1, size 8)
</Heading>
<Heading as="h2" size="6">
  Section heading (h2, size 6)
</Heading>
<Heading as="h3" size="5">
  Subsection heading (h3, size 5)
</Heading>`,
        Demo: HeadingDemos,
      },
      {
        id: 'brand-scale',
        title: 'Brand type scale',
        code: `import { Heading, Text } from '@nrx/ui';

{/* Specimens at true size — D1 120, H1 54, H3 32, H5 24, H7 18 */}
<Heading as="h1" scale="d1">Aa</Heading>
<Heading as="h1" scale="h1">Aa</Heading>
<Heading as="h2" scale="h3">Aa</Heading>
<Text as="p" size="3">Aa</Text>`,
        Demo: HeadingScaleDemos,
      },
      {
        id: 'display-samples',
        title: 'Display samples',
        code: `import { Heading } from '@nrx/ui';

<Heading as="h1" scale="h1">Hi, John!</Heading>
<Heading as="h2" scale="h3">Unlock food as medicine</Heading>
<Heading as="h3" scale="h5" weight="medium">
  We believe in the power of delicious, healthy food.
</Heading>`,
        Demo: HeadingDisplayDemos,
      },
    ],
  },
  {
    name: 'Modal',
    slug: 'modal',
    category: 'surfaces',
    status: 'stable',
    description: 'Accessible modal dialog with overlay, escape-to-close, and focus management.',
    importExample: "import { Modal } from '@nrx/ui';",
    props: [
      {
        name: 'open',
        type: 'boolean',
        required: true,
        description: 'Whether the modal is visible.',
      },
      {
        name: 'onClose',
        type: '() => void',
        required: true,
        description: 'Called when the modal should close.',
      },
      {
        name: 'aria-label',
        type: 'string',
        description: 'Accessible label when no visible title.',
      },
      {
        name: 'size',
        type: "'default' | 'wide'",
        description:
          'Dialog width. `default` (28rem) for confirmations; `wide` (most of the viewport) for content-heavy views.',
      },
    ],
    sections: [
      {
        id: 'basic',
        title: 'Modal dialog',
        code: `import { useState } from 'react';
import { Button, Heading, Modal, Text } from '@nrx/ui';

const [open, setOpen] = useState(false);

<Button tone="primary" onClick={() => setOpen(true)}>
  Open modal
</Button>
<Modal open={open} onClose={() => setOpen(false)} aria-label="Example modal">
  <Heading as="h2" size="5">
    Confirm action
  </Heading>
  <Text as="p" size="2" color="muted">
    This modal closes on Escape or overlay click.
  </Text>
  <Modal.Footer>
    <Button tone="secondary" onClick={() => setOpen(false)}>
      Cancel
    </Button>
    <Button tone="primary" onClick={() => setOpen(false)}>
      Confirm
    </Button>
  </Modal.Footer>
</Modal>`,
        Demo: ModalDemos,
      },
    ],
  },
  {
    name: 'ConfirmDialog',
    slug: 'confirm-dialog',
    category: 'feedback',
    status: 'stable',
    description:
      'Accessible confirm/cancel dialog built on Modal for destructive or irreversible actions.',
    importExample: "import { ConfirmDialog } from '@nrx/ui';",
    props: [
      {
        name: 'open',
        type: 'boolean',
        required: true,
        description: 'Whether the dialog is visible.',
      },
      {
        name: 'title',
        type: 'string',
        required: true,
        description: 'Dialog heading.',
      },
      {
        name: 'onConfirm',
        type: '() => void',
        required: true,
        description: 'Called when the user confirms.',
      },
      {
        name: 'onCancel',
        type: '() => void',
        required: true,
        description: 'Called when the user cancels or dismisses.',
      },
      {
        name: 'destructive',
        type: 'boolean',
        description: 'Styles the confirm action as destructive (danger tone).',
      },
      {
        name: 'confirmLabel',
        type: 'string',
        default: "'Confirm'",
        description: 'Label for the confirm button.',
      },
      {
        name: 'cancelLabel',
        type: 'string',
        default: "'Cancel'",
        description: 'Label for the cancel button.',
      },
      {
        name: 'busy',
        type: 'boolean',
        description: 'Disables both buttons and marks confirm as busy.',
      },
    ],
    sections: [
      {
        id: 'basic',
        title: 'Confirm dialog',
        code: `import { useState } from 'react';
import { Button, ConfirmDialog } from '@nrx/ui';

const [open, setOpen] = useState(false);

<Button tone="primary" onClick={() => setOpen(true)}>
  Delete item
</Button>
<ConfirmDialog
  open={open}
  title="Delete item"
  destructive
  confirmLabel="Delete"
  onCancel={() => setOpen(false)}
  onConfirm={() => setOpen(false)}
>
  This action retires the item.
</ConfirmDialog>`,
        Demo: ConfirmDialogDemos,
      },
    ],
  },
  {
    name: 'Table',
    slug: 'table',
    category: 'data-display',
    status: 'stable',
    description: 'Compound table primitive for data-heavy admin screens.',
    importExample: "import { Table } from '@nrx/ui';",
    props: [
      {
        name: 'Table.ScrollArea',
        type: 'Component',
        description:
          'Scroll shell. Pass loading / loadingLabel to show Spinner instead of children.',
      },
      {
        name: 'loading',
        type: 'boolean',
        default: 'false',
        description: 'On Table.ScrollArea — replaces children with a centered Spinner.',
      },
      {
        name: 'loadingLabel',
        type: 'string',
        default: "'Loading'",
        description: 'Accessible label for the loading Spinner.',
      },
      { name: 'Table.Root', type: 'Component', description: 'Table wrapper element.' },
      {
        name: 'Table.Header / Body / Row',
        type: 'Component',
        description: 'Structural table parts.',
      },
      {
        name: 'Table.ColumnHeaderCell / Cell',
        type: 'Component',
        description: 'Header and data cells.',
      },
    ],
    sections: [
      {
        id: 'basic',
        title: 'Data table',
        code: `import { Badge, Table } from '@nrx/ui';

<Table.Root>
  <Table.Header>
    <Table.Row>
      <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
      <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
    </Table.Row>
  </Table.Header>
  <Table.Body>
    <Table.Row>
      <Table.Cell>Alice</Table.Cell>
      <Table.Cell>
        <Badge variant="success">Active</Badge>
      </Table.Cell>
    </Table.Row>
  </Table.Body>
</Table.Root>`,
        Demo: TableDemos,
      },
      {
        id: 'with-actions',
        title: 'Table with actions',
        code: `import { Badge, DropdownMenu, Flex, IconButton, Table, Tooltip } from '@nrx/ui';

<Table.Root>
  <Table.Header>
    <Table.Row>
      <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
      <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
      <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
    </Table.Row>
  </Table.Header>
  <Table.Body>
    <Table.Row>
      <Table.Cell>Health intake</Table.Cell>
      <Table.Cell><Badge variant="success">Active</Badge></Table.Cell>
      <Table.Cell>
        <Flex gap="2">
          <Tooltip content="Edit survey">
            <IconButton tone="neutral" aria-label="Edit">✎</IconButton>
          </Tooltip>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <IconButton tone="neutral" aria-label="More">⋮</IconButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              <DropdownMenu.Item tone="danger">Delete</DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </Flex>
      </Table.Cell>
    </Table.Row>
  </Table.Body>
</Table.Root>`,
        Demo: TableWithActionsDemos,
      },
      {
        id: 'loading',
        title: 'Loading state',
        code: `import { Table, loadingMessage } from '@nrx/ui';

<Table.ScrollArea
  loading
  loadingLabel={loadingMessage({ resource: 'row', operation: 'list' })}
>
  <Table.Root>…</Table.Root>
</Table.ScrollArea>`,
        Demo: TableLoadingDemos,
      },
    ],
  },
  {
    name: 'Select',
    slug: 'select',
    category: 'inputs',
    status: 'stable',
    description: 'Controlled native select styled like TextInput.',
    importExample: "import { Select } from '@nrx/ui';",
    props: [
      { name: 'value', type: 'string', required: true, description: 'Current selected value.' },
      {
        name: 'onChange',
        type: '(value: string) => void',
        required: true,
        description: 'Called when selection changes.',
      },
    ],
    sections: [
      {
        id: 'select',
        title: 'Select dropdown',
        code: `import { Select } from '@nrx/ui';

const [status, setStatus] = useState('open');

<Select value={status} onChange={setStatus} aria-label="Status">
  <option value="open">Open</option>
  <option value="in-progress">In progress</option>
  <option value="closed">Closed</option>
</Select>`,
        Demo: SelectDemos,
      },
    ],
  },
  {
    name: 'Textarea',
    slug: 'textarea',
    category: 'inputs',
    status: 'stable',
    description: 'Controlled native textarea styled like TextInput.',
    importExample: "import { Textarea } from '@nrx/ui';",
    props: [
      { name: 'value', type: 'string', required: true, description: 'Current textarea value.' },
      {
        name: 'onChange',
        type: '(value: string) => void',
        required: true,
        description: 'Called when value changes.',
      },
    ],
    sections: [
      {
        id: 'textarea',
        title: 'Textarea input',
        code: `import { Textarea } from '@nrx/ui';

const [notes, setNotes] = useState('Member prefers morning appointments.');

<Textarea
  value={notes}
  onChange={setNotes}
  placeholder="Enter notes…"
  aria-label="Notes"
/>`,
        Demo: TextareaDemos,
      },
    ],
  },
  {
    name: 'Accordion',
    slug: 'accordion',
    category: 'surfaces',
    status: 'stable',
    description:
      'Expandable panel primitive for stacked sections such as survey pages and questions.',
    importExample: "import { Accordion } from '@nrx/ui';",
    props: [
      {
        name: 'Accordion.Root',
        type: 'Component',
        description: 'Accordion container with expanded state.',
      },
      {
        name: 'Accordion.Item',
        type: 'Component',
        description: 'Single panel; requires unique value.',
      },
      { name: 'Accordion.Summary', type: 'Component', description: 'Clickable header button.' },
      { name: 'Accordion.Content', type: 'Component', description: 'Collapsible panel body.' },
    ],
    sections: [
      {
        id: 'basic',
        title: 'Accordion panels',
        code: `import { Accordion, Text } from '@nrx/ui';

<Accordion.Root defaultExpanded="page-1">
  <Accordion.Item value="page-1">
    <Accordion.Summary>Page 1</Accordion.Summary>
    <Accordion.Content>
      <Text size="2">Page content</Text>
    </Accordion.Content>
  </Accordion.Item>
</Accordion.Root>`,
        Demo: AccordionDemos,
      },
    ],
  },
  {
    name: 'Autocomplete',
    slug: 'autocomplete',
    category: 'inputs',
    status: 'stable',
    description:
      'Filterable combobox for typeahead selection of question types, banks, and logic targets.',
    importExample: "import { Autocomplete } from '@nrx/ui';",
    props: [
      { name: 'value', type: 'string', required: true, description: 'Selected option value.' },
      {
        name: 'onChange',
        type: '(value: string) => void',
        required: true,
        description: 'Called when selection changes.',
      },
      {
        name: 'options',
        type: 'AutocompleteOption[]',
        required: true,
        description: 'Selectable value/label pairs.',
      },
      { name: 'placeholder', type: 'string', description: 'Input placeholder.' },
      {
        name: 'filterOptions',
        type: 'boolean',
        default: 'true',
        description: 'Filter list as user types.',
      },
    ],
    sections: [
      {
        id: 'basic',
        title: 'Typeahead select',
        code: `import { Autocomplete } from '@nrx/ui';

<Autocomplete
  value={type}
  onChange={setType}
  aria-label="Question type"
  options={[
    { value: 'text', label: 'Text' },
    { value: 'choice', label: 'Single choice' },
  ]}
/>`,
        Demo: AutocompleteDemos,
      },
    ],
  },
  {
    name: 'MultiSelect',
    slug: 'multi-select',
    category: 'inputs',
    status: 'stable',
    description:
      'Multi-value dropdown with removable chips for programs, workflows, and other tag sets.',
    importExample: "import { MultiSelect } from '@nrx/ui';",
    props: [
      {
        name: 'value',
        type: 'readonly string[]',
        required: true,
        description: 'Selected option values.',
      },
      {
        name: 'onChange',
        type: '(next: string[]) => void',
        required: true,
        description: 'Called when the selection set changes.',
      },
      {
        name: 'options',
        type: 'MultiSelectOption[]',
        required: true,
        description: 'Selectable value/label pairs.',
      },
      { name: 'placeholder', type: 'string', description: 'Shown when nothing is selected.' },
      { name: 'disabled', type: 'boolean', description: 'Disables the control.' },
    ],
    sections: [
      {
        id: 'basic',
        title: 'Chip multi-select',
        code: `import { MultiSelect } from '@nrx/ui';

<MultiSelect
  value={programs}
  onChange={setPrograms}
  aria-label="Programs"
  options={[
    { value: 'hola', label: 'HOLA' },
    { value: 'bcmn', label: 'BCMN' },
  ]}
/>`,
        Demo: MultiSelectDemos,
      },
    ],
  },
  {
    name: 'Checkbox',
    slug: 'checkbox',
    category: 'inputs',
    status: 'stable',
    description:
      'Native checkbox with a coral checked fill and white check mark for settings, enable-logic targets, and multi-select preview.',
    importExample: "import { Checkbox, FormControlLabel } from '@nrx/ui';",
    props: [
      { name: 'checked', type: 'boolean', description: 'Controlled checked state.' },
      { name: 'onChange', type: 'ChangeEventHandler', description: 'Native change handler.' },
      { name: 'disabled', type: 'boolean', description: 'Disables the checkbox.' },
    ],
    sections: [
      {
        id: 'labeled',
        title: 'Checkbox with label',
        code: `import { Checkbox, FormControlLabel } from '@nrx/ui';

<FormControlLabel
  control={<Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
  label="Enable conditional logic"
/>`,
        Demo: CheckboxDemos,
      },
    ],
  },
  {
    name: 'RadioGroup',
    slug: 'radio-group',
    category: 'inputs',
    status: 'stable',
    description:
      'Mutually exclusive radio options with a coral selected ring and centre dot for single-choice survey preview and forms.',
    importExample: "import { Radio, RadioGroup } from '@nrx/ui';",
    props: [
      { name: 'value', type: 'string', required: true, description: 'Selected option value.' },
      {
        name: 'onChange',
        type: '(value: string) => void',
        required: true,
        description: 'Called when selection changes.',
      },
      {
        name: 'Radio value',
        type: 'string',
        required: true,
        description: 'Option value on each Radio.',
      },
      { name: 'Radio label', type: 'ReactNode', description: 'Visible label for each Radio.' },
    ],
    sections: [
      {
        id: 'basic',
        title: 'Radio group',
        code: `import { Radio, RadioGroup } from '@nrx/ui';

<RadioGroup value={freq} onChange={setFreq} aria-label="Frequency">
  <Radio value="daily" label="Daily" />
  <Radio value="weekly" label="Weekly" />
</RadioGroup>`,
        Demo: RadioGroupDemos,
      },
    ],
  },
  {
    name: 'IconButton',
    slug: 'icon-button',
    category: 'inputs',
    status: 'stable',
    description: 'Compact icon-only button for delete, collapse, and sidebar controls.',
    importExample: "import { IconButton, Tooltip } from '@nrx/ui';",
    props: [
      {
        name: 'aria-label',
        type: 'string',
        required: true,
        description: 'Accessible name (no visible text).',
      },
      {
        name: 'tone',
        type: "'neutral' | 'danger'",
        default: "'neutral'",
        description: 'Visual emphasis.',
      },
      { name: 'children', type: 'ReactNode', required: true, description: 'Icon content.' },
    ],
    sections: [
      {
        id: 'with-tooltip',
        title: 'Icon button with tooltip',
        code: `import { IconButton, Tooltip } from '@nrx/ui';

<Tooltip content="Delete question">
  <IconButton tone="danger" aria-label="Delete question">
    ×
  </IconButton>
</Tooltip>`,
        Demo: IconButtonDemos,
      },
    ],
  },
  {
    name: 'Tooltip',
    slug: 'tooltip',
    category: 'data-display',
    status: 'stable',
    description: 'Hover/focus tooltip for icon-only controls and truncated labels.',
    importExample: "import { Tooltip } from '@nrx/ui';",
    props: [
      { name: 'content', type: 'string', required: true, description: 'Tooltip text.' },
      { name: 'children', type: 'ReactNode', required: true, description: 'Trigger element.' },
    ],
    sections: [
      {
        id: 'basic',
        title: 'Tooltip',
        code: `import { Button, Tooltip } from '@nrx/ui';

<Tooltip content="Save changes before leaving">
  <Button tone="secondary">Hover for hint</Button>
</Tooltip>`,
        Demo: TooltipDemos,
      },
    ],
  },
  {
    name: 'Tabs',
    slug: 'tabs',
    category: 'navigation',
    status: 'stable',
    description: 'Tabbed panels for sidebar sections such as outline and enable-logic views.',
    importExample: "import { Tabs } from '@nrx/ui';",
    props: [
      { name: 'Tabs.Root', type: 'Component', description: 'Tabs container with value state.' },
      { name: 'Tabs.List', type: 'Component', description: 'Tab button row.' },
      { name: 'Tabs.Tab', type: 'Component', description: 'Individual tab trigger.' },
      { name: 'Tabs.Panel', type: 'Component', description: 'Content for active tab.' },
    ],
    sections: [
      {
        id: 'basic',
        title: 'Tab panels',
        code: `import { Tabs, Text } from '@nrx/ui';

<Tabs.Root value={tab} onValueChange={setTab}>
  <Tabs.List aria-label="Sidebar">
    <Tabs.Tab value="outline">Outline</Tabs.Tab>
    <Tabs.Tab value="logic">Enable logic</Tabs.Tab>
  </Tabs.List>
  <Tabs.Panel value="outline"><Text size="2">Outline tree</Text></Tabs.Panel>
</Tabs.Root>`,
        Demo: TabsDemos,
      },
    ],
  },
  {
    name: 'Separator',
    slug: 'separator',
    category: 'data-display',
    status: 'stable',
    description: 'Visually or semantically separates content.',
    importExample: "import { Separator } from '@nrx/ui';",
    props: [
      {
        name: 'orientation',
        type: "'horizontal' | 'vertical'",
        default: "'horizontal'",
        description: 'Separator direction.',
      },
      {
        name: 'size',
        type: "'1' | '2' | '3' | '4'",
        default: "'1'",
        description: 'Spacing scale around the separator.',
      },
    ],
    sections: [
      {
        id: 'basic',
        title: 'Separator',
        code: `import { Flex, Separator, Text } from '@nrx/ui';

<Text size="2">Tools for building high-quality, accessible UI.</Text>
<Separator size="3" />
<Flex gap="3" align="center">
  Themes <Separator orientation="vertical" /> Primitives
</Flex>`,
        Demo: SeparatorDemos,
      },
    ],
  },
  {
    name: 'List',
    slug: 'list',
    category: 'data-display',
    status: 'stable',
    description:
      'Vertical list for sidebar navigation: List container, ListItemButton rows, and ListSection expandable groups.',
    importExample:
      "import { List, ListItemButton, ListItemIcon, ListItemText, ListSection } from '@nrx/ui';",
    props: [
      {
        name: 'ListItemButton.active',
        type: 'boolean',
        description: 'Highlights the current route.',
      },
      { name: 'ListItemButton.href', type: 'string', description: 'Renders as anchor when set.' },
      { name: 'ListItemButton.indent', type: '0 | 1 | 2', description: 'Nested item indentation.' },
      {
        name: 'ListSection.defaultOpen',
        type: 'boolean',
        description: 'Initial expanded state when uncontrolled.',
      },
      { name: 'ListSection.open', type: 'boolean', description: 'Controlled expanded state.' },
      {
        name: 'ListSection.onOpenChange',
        type: '(open: boolean) => void',
        description: 'Called when the section is expanded or collapsed.',
      },
    ],
    sections: [
      {
        id: 'sidebar',
        title: 'Sidebar navigation',
        code: `import { List, ListItemButton, ListItemIcon, ListItemText, ListSection } from '@nrx/ui';

<List>
  <ListItemButton href="/" active>
    <ListItemIcon>⌂</ListItemIcon>
    <ListItemText primary="Dashboard" />
  </ListItemButton>
  <ListSection label="Survey Builder" defaultOpen>
    <ListItemButton href="/surveys" indent={1}>Surveys</ListItemButton>
  </ListSection>
</List>`,
        Demo: ListDemos,
      },
    ],
  },
  {
    name: 'DropdownMenu',
    slug: 'dropdown-menu',
    category: 'navigation',
    status: 'stable',
    description: 'Menu representing a set of actions, triggered by a button.',
    importExample: "import { DropdownMenu, Button } from '@nrx/ui';",
    props: [
      { name: 'DropdownMenu.Root', type: 'Component', description: 'Menu state container.' },
      {
        name: 'DropdownMenu.Trigger',
        type: 'Component',
        description: 'Button that opens the menu.',
      },
      { name: 'DropdownMenu.Content', type: 'Component', description: 'Portaled menu panel.' },
      { name: 'DropdownMenu.Item', type: 'Component', description: 'Selectable menu action.' },
      {
        name: 'DropdownMenu.Item.tone',
        type: "'default' | 'danger'",
        description: 'Visual intent for menu actions. Use danger for irreversible operations.',
      },
    ],
    sections: [
      {
        id: 'basic',
        title: 'Dropdown menu',
        code: `import { Button, DropdownMenu } from '@nrx/ui';

<DropdownMenu.Root>
  <DropdownMenu.Trigger>
    <Button tone="secondary">Options</Button>
  </DropdownMenu.Trigger>
  <DropdownMenu.Content>
    <DropdownMenu.Item>Edit</DropdownMenu.Item>
    <DropdownMenu.Item>Duplicate</DropdownMenu.Item>
    <DropdownMenu.Separator />
    <DropdownMenu.Item tone="danger">Delete</DropdownMenu.Item>
  </DropdownMenu.Content>
</DropdownMenu.Root>`,
        Demo: DropdownMenuDemos,
      },
    ],
  },
  {
    name: 'Chip',
    slug: 'chip',
    category: 'data-display',
    status: 'stable',
    description:
      'Compact tag for filters, program selections, and deletable option chips. Semantic tones match Alert.',
    importExample: "import { Chip } from '@nrx/ui';",
    props: [
      { name: 'children', type: 'ReactNode', required: true, description: 'Chip label.' },
      { name: 'onDelete', type: '() => void', description: 'Renders remove button when set.' },
      {
        name: 'variant',
        type: "'filled' | 'outlined'",
        default: "'filled'",
        description: 'Fill style.',
      },
      {
        name: 'tone',
        type: "'neutral' | 'info' | 'warning' | 'danger' | 'success'",
        default: "'neutral'",
        description: 'Semantic color tone (Alert-aligned).',
      },
    ],
    sections: [
      {
        id: 'deletable',
        title: 'Deletable chips',
        code: `import { Chip, Flex } from '@nrx/ui';

<Flex gap="2">
  <Chip onDelete={() => remove('Draft')}>Draft</Chip>
  <Chip variant="outlined">Read-only</Chip>
</Flex>`,
        Demo: ChipDemos,
      },
      {
        id: 'tones',
        title: 'Tones',
        code: `import { Chip, Flex } from '@nrx/ui';

<Flex gap="2" wrap>
  <Chip tone="neutral">Neutral</Chip>
  <Chip tone="info">Info</Chip>
  <Chip tone="warning">Warning</Chip>
  <Chip tone="danger">Danger</Chip>
  <Chip tone="success">Success</Chip>
</Flex>
<Flex gap="2" wrap>
  <Chip tone="info" variant="outlined">Info</Chip>
  <Chip tone="danger" variant="outlined">Danger</Chip>
</Flex>`,
        Demo: ChipToneDemos,
      },
    ],
  },
  {
    name: 'FormLabel',
    slug: 'form-label',
    category: 'inputs',
    status: 'stable',
    description:
      'FormLabel, FormGroup, and FormHelperText atoms for grouped field labels and hints.',
    importExample: "import { FormLabel, FormGroup, FormHelperText } from '@nrx/ui';",
    props: [
      { name: 'FormLabel.required', type: 'boolean', description: 'Shows required indicator.' },
      {
        name: 'FormHelperText.error',
        type: 'boolean',
        description: 'Error styling for validation text.',
      },
    ],
    sections: [
      {
        id: 'field-kit',
        title: 'Label and helper',
        code: `import { FormGroup, FormHelperText, FormLabel, TextInput } from '@nrx/ui';

<FormGroup>
  <FormLabel required>Question prompt</FormLabel>
  <TextInput value={q} onChange={setQ} />
  <FormHelperText>Shown to members during the survey.</FormHelperText>
</FormGroup>`,
        Demo: FormAtomsDemos,
      },
    ],
  },
  {
    name: 'Breadcrumbs',
    slug: 'breadcrumbs',
    category: 'navigation',
    status: 'stable',
    description: 'Hierarchical page trail for builder and list/detail navigation.',
    importExample: "import { Breadcrumbs } from '@nrx/ui';",
    props: [
      {
        name: 'items',
        type: 'BreadcrumbItem[]',
        required: true,
        description: 'Trail segments with optional href.',
      },
    ],
    sections: [
      {
        id: 'basic',
        title: 'Breadcrumb trail',
        code: `import { Breadcrumbs } from '@nrx/ui';

<Breadcrumbs
  items={[
    { label: 'Surveys', href: '/surveys' },
    { label: 'Builder' },
  ]}
/>`,
        Demo: BreadcrumbsDemos,
      },
    ],
  },
  {
    name: 'Snackbar',
    slug: 'snackbar',
    category: 'feedback',
    status: 'stable',
    description: 'Transient feedback banner for save/publish actions.',
    importExample: "import { Snackbar } from '@nrx/ui';",
    props: [
      { name: 'open', type: 'boolean', required: true, description: 'Visibility.' },
      { name: 'message', type: 'ReactNode', required: true, description: 'Feedback text.' },
      { name: 'onClose', type: '() => void', required: true, description: 'Dismiss handler.' },
      {
        name: 'tone',
        type: "'info' | 'success' | 'warning' | 'danger'",
        default: "'info'",
        description: 'Semantic styling tone.',
      },
      { name: 'autoHideDuration', type: 'number', description: 'Auto-dismiss after ms.' },
    ],
    sections: [
      {
        id: 'basic',
        title: 'Snackbar',
        code: `import { Snackbar } from '@nrx/ui';

<Snackbar
  open={open}
  message="Survey saved successfully."
  tone="success"
  onClose={() => setOpen(false)}
  autoHideDuration={4000}
/>`,
        Demo: SnackbarDemos,
      },
    ],
  },
  {
    name: 'SuiteShell',
    slug: 'suite-shell',
    category: 'surfaces',
    status: 'planned',
    description: 'Suite-level shell with title — planned for Betty portal work.',
    importExample: "import { SuiteShell } from '@nrx/ui/planned';",
    props: [
      {
        name: 'title',
        type: 'string',
        required: true,
        description: 'Suite title displayed in the shell.',
      },
      {
        name: 'children',
        type: 'ReactNode',
        description: 'Suite content.',
      },
    ],
    sections: [],
  },
  {
    name: 'BrandMark',
    slug: 'brand-mark',
    category: 'data-display',
    status: 'planned',
    description: 'NourishedRx brand mark — planned.',
    importExample: "import { BrandMark } from '@nrx/ui/planned';",
    props: [
      {
        name: 'size',
        type: "'sm' | 'md' | 'lg'",
        default: "'md'",
        description: 'Display size of the brand mark.',
      },
    ],
    sections: [],
  },
];

/** Look up a component doc entry by URL slug. */
export function getComponentBySlug(slug: string): ComponentDocEntry | undefined {
  return COMPONENT_REGISTRY.find((entry) => entry.slug === slug);
}

/** Stable components that have usage documentation pages. */
export function getStableComponents(): ComponentDocEntry[] {
  return COMPONENT_REGISTRY.filter((entry) => entry.status === 'stable');
}

/** Group registry entries by category for navigation and grids. */
export function getComponentsByCategory(): Map<ComponentCategory, ComponentDocEntry[]> {
  const map = new Map<ComponentCategory, ComponentDocEntry[]>();
  for (const entry of COMPONENT_REGISTRY) {
    const list = map.get(entry.category) ?? [];
    list.push(entry);
    map.set(entry.category, list);
  }
  for (const list of map.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }
  return map;
}
